import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmt, hasPerm, initials } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Search, ChevronDown, ChevronRight, Users, Phone, MapPin, HandCoins, Eye, MoreHorizontal, Pencil, UserPlus, Trash2, Briefcase, Wallet } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { StudentForm } from "@/pages/Students";
import CollectFeeDialog from "@/pages/CollectFeeDialog";
import { toast } from "sonner";

export default function ParentSibling() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState({});
  const [collectId, setCollectId] = useState(null);
  const [studentEdit, setStudentEdit] = useState(null);
  const [addWardParent, setAddWardParent] = useState(null);
  const [studentFormOpen, setStudentFormOpen] = useState(false);
  const [delWard, setDelWard] = useState(null);

  const canCollect = hasPerm(user, "collect_fee");
  const canEdit = hasPerm(user, "edit_student");
  const canDelete = hasPerm(user, "delete");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/parents").then((r) => setRows(r.data)).catch(() => toast.error("Could not load families")).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/classes").then((r) => setClasses(r.data)).catch(() => {}); }, []);

  const openEdit = async (w) => {
    try {
      const r = await api.get(`/students/${w.id}`);
      setStudentEdit(r.data); setAddWardParent(null); setStudentFormOpen(true);
    } catch (e) { toast.error("Could not load student"); }
  };
  const openAddWard = (g) => {
    setStudentEdit(null);
    setAddWardParent({ father_name: g.guardian_name, phone: g.phone, alt_phone: g.alt_phone, address: g.address });
    setStudentFormOpen(true);
  };
  const doDeleteWard = async () => {
    try {
      await api.delete(`/students/${delWard.id}`, { data: { reason: "Removed from Parent/Sibling", leave_status: "Left", leave_date: "" } });
      toast.success("Ward removed"); setDelWard(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const q = search.toLowerCase();
  const filtered = rows.filter((g) =>
    !q || (g.guardian_name || "").toLowerCase().includes(q) || (g.phone || "").includes(q) ||
    g.wards.some((w) => (w.name || "").toLowerCase().includes(q)));

  const toggle = (k) => setOpen((p) => ({ ...p, [k]: !p[k] }));
  const totalFamilies = filtered.length;
  const totalStudents = filtered.reduce((s, g) => s + (g.ward_count || 0), 0);
  const totalFee = filtered.reduce((s, g) => s + (g.total_payable || 0), 0);
  const totalDiscount = filtered.reduce((s, g) => s + g.wards.reduce((a, w) => a + (w.fees_summary.discount || 0), 0), 0);
  const totalPaid = filtered.reduce((s, g) => s + (g.total_paid || 0), 0);
  const totalDue = filtered.reduce((s, g) => s + (g.total_due || 0), 0);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 flex items-center gap-2"><Users className="text-indigo-500" size={26} />Parent / Sibling</h1>
          <p className="text-slate-500">Families grouped by guardian — wards and their fees fetched automatically.</p>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-slate-200 bg-white px-4 py-2.5" data-testid="parents-total-due">
          <span className="text-sm font-semibold text-slate-700">{totalFamilies} families</span>
          <span className="text-slate-300">·</span>
          <span className="text-sm font-semibold text-slate-700" data-testid="parents-total-students">{totalStudents} students</span>
          <span className="text-slate-300">·</span>
          <span className="text-sm text-slate-500">Total Fee: <b className="tabular text-slate-800">{fmt(totalFee)}</b></span>
          <span className="text-sm text-slate-500">Discount: <b className="tabular text-amber-600">{fmt(totalDiscount)}</b></span>
          <span className="text-sm text-slate-500">Paid: <b className="tabular text-emerald-600">{fmt(totalPaid)}</b></span>
          <span className="text-sm text-slate-500">Due: <b className="tabular text-rose-600">{fmt(totalDue)}</b></span>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-3">
        <div className="relative max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9 h-9" placeholder="Search guardian, phone or ward name…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="parents-search" />
        </div>
      </div>

      {loading ? <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div> :
        filtered.length === 0 ? <div className="py-16 text-center text-slate-400 bg-white rounded-xl border border-slate-200"><Users size={40} className="mx-auto mb-2 opacity-40" />No families found.</div> : (
          <div className="space-y-3">
            {filtered.map((g) => {
              const isOpen = open[g.key] ?? (filtered.length <= 3);
              return (
                <div key={g.key} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`parent-card-${g.key}`}>
                  <button onClick={() => toggle(g.key)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left" data-testid={`parent-toggle-${g.key}`}>
                    {isOpen ? <ChevronDown size={18} className="text-slate-400 shrink-0" /> : <ChevronRight size={18} className="text-slate-400 shrink-0" />}
                    <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-sm font-semibold shrink-0">{initials(g.guardian_name || "P")}</div>
                    <div className="min-w-0 flex-1">
                      <span onClick={(e) => { e.stopPropagation(); nav(`/parents/${encodeURIComponent(g.key)}`); }} className="font-semibold text-slate-800 truncate hover:text-indigo-600 hover:underline cursor-pointer" data-testid={`parent-name-${g.key}`}>{g.guardian_name || "Unknown Guardian"}</span>
                      <div className="text-xs text-slate-400 flex items-center gap-3 flex-wrap">
                        {g.phone && <span className="flex items-center gap-1"><Phone size={12} />{g.phone}{g.alt_phone ? ` / ${g.alt_phone}` : ""}</span>}
                        {g.address && <span className="flex items-center gap-1 truncate max-w-[220px]"><MapPin size={12} />{g.address}</span>}
                        {g.profession && <span className="flex items-center gap-1"><Briefcase size={12} />{g.profession}</span>}
                        {g.income && <span className="flex items-center gap-1"><Wallet size={12} />{g.income}</span>}
                      </div>
                    </div>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 shrink-0">{g.ward_count} ward{g.ward_count > 1 ? "s" : ""}</span>
                    <div className="text-right shrink-0 w-28">
                      <div className={`font-bold tabular ${g.total_due > 0 ? "text-rose-600" : "text-emerald-600"}`}>{fmt(g.total_due)}</div>
                      <div className="text-[10px] uppercase tracking-wide text-slate-400">Due</div>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="border-t border-slate-100 overflow-x-auto">
                      <table className="w-full text-sm border-collapse min-w-[920px]">
                        <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                          <tr>{["Ward", "Total Fees", "Discount", "Payable", "Paid", "Due", "Follow up", "Action"].map((h) => <th key={h} className={`px-3 py-2 ${h === "Action" ? "text-center" : "text-left"} ${["Total Fees", "Discount", "Payable", "Paid", "Due"].includes(h) ? "text-right" : ""}`}>{h}</th>)}</tr>
                        </thead>
                        <tbody>
                          {g.wards.map((w) => (
                            <tr key={w.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`ward-row-${w.id}`}>
                              <td className="px-3 py-2.5">
                                <div className="flex items-center gap-2.5">
                                  <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 overflow-hidden flex items-center justify-center text-xs font-semibold shrink-0">{w.photo ? <img src={w.photo} alt={w.name} className="w-full h-full object-cover" /> : initials(w.name)}</div>
                                  <div><div className="font-medium text-slate-800">{w.name}</div><div className="text-xs text-slate-400">{w.admission_number} · {w.class_name}{w.section ? ` ${w.section}` : ""}</div></div>
                                </div>
                              </td>
                              <td className="px-3 py-2.5 text-right tabular text-slate-700">{fmt(w.fees_summary.total_payable)}</td>
                              <td className="px-3 py-2.5 text-right tabular text-amber-600">{fmt(w.fees_summary.discount)}</td>
                              <td className="px-3 py-2.5 text-right tabular text-slate-700">{fmt(w.fees_summary.total_payable - w.fees_summary.discount)}</td>
                              <td className="px-3 py-2.5 text-right tabular text-emerald-600">{fmt(w.fees_summary.paid)}</td>
                              <td className={`px-3 py-2.5 text-right tabular font-semibold ${w.fees_summary.due > 0 ? "text-rose-600" : "text-emerald-600"}`}>{fmt(w.fees_summary.due)}</td>
                              <td className="px-3 py-2.5 text-slate-600" data-testid={`followup-${w.id}`}>
                                {w.promise_date || w.promise_amount ? (
                                  <div className="text-xs"><div>{w.promise_date || "—"}</div><div className="tabular text-slate-500">{fmt(w.promise_amount || 0)}</div></div>
                                ) : <span className="text-slate-300">—</span>}
                              </td>
                              <td className="px-3 py-2.5">
                                <div className="flex items-center justify-center">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button size="sm" variant="ghost" className="h-8 px-2" data-testid={`ward-actions-${w.id}`}><MoreHorizontal size={16} /></Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-44">
                                      {canCollect && <DropdownMenuItem onClick={() => nav(`/fees/collect/${w.id}`)} data-testid={`collect-${w.id}`}><HandCoins size={14} className="mr-2 text-indigo-600" />Collect Fees</DropdownMenuItem>}
                                      <DropdownMenuItem onClick={() => nav(`/students/${w.id}`)} data-testid={`view-${w.id}`}><Eye size={14} className="mr-2" />View</DropdownMenuItem>
                                      {canEdit && <DropdownMenuItem onClick={() => openEdit(w)} data-testid={`edit-${w.id}`}><Pencil size={14} className="mr-2" />Edit</DropdownMenuItem>}
                                      {canEdit && <DropdownMenuItem onClick={() => openAddWard(g)} data-testid={`add-ward-${g.key}`}><UserPlus size={14} className="mr-2 text-emerald-600" />Add New Ward</DropdownMenuItem>}
                                      {canDelete && <><DropdownMenuSeparator /><DropdownMenuItem onClick={() => setDelWard(w)} className="text-rose-600 focus:text-rose-600" data-testid={`delete-ward-${w.id}`}><Trash2 size={14} className="mr-2" />Delete Ward</DropdownMenuItem></>}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

      <CollectFeeDialog open={!!collectId} onOpenChange={(o) => !o && setCollectId(null)} studentId={collectId} onCollected={load} />

      <StudentForm
        open={studentFormOpen}
        onOpenChange={(o) => { setStudentFormOpen(o); if (!o) { setStudentEdit(null); setAddWardParent(null); } }}
        classes={classes}
        edit={studentEdit}
        initial={addWardParent}
        addTitle="Add Ward — New Student"
        onSaved={load}
      />

      <AlertDialog open={!!delWard} onOpenChange={(o) => !o && setDelWard(null)}>
        <AlertDialogContent data-testid="delete-ward-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Ward?</AlertDialogTitle>
            <AlertDialogDescription>This moves <b>{delWard?.name}</b> to Left Students. You can restore them later from the Left Students page.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={doDeleteWard} data-testid="delete-ward-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
